import { useEffect, useRef, useState } from 'react';
import * as handOrderGrantAnimationDetector from './handOrderGrantAnimationDetector';

const {
  collectHandOrderGrantTriggerKeys,
  findNewHandOrderGrantEntries,
  resolveProviderUnitSlot,
} = handOrderGrantAnimationDetector;

const ANIMATION_TOTAL_MS = 1300;

function snapshotBattlefieldUnits(gameState) {
  const units = new Map();
  if (!gameState?.state) {
    return units;
  }

  const allPlayerIds = gameState.players || Object.keys(gameState.state || {});
  allPlayerIds.forEach((playerId) => {
    const player = gameState.state[playerId];
    if (!player?.playedOrders) {
      return;
    }

    ['front', 'flank'].forEach((zone) => {
      const row = player.playedOrders[zone];
      if (!Array.isArray(row)) {
        return;
      }
      row.forEach((unit, index) => {
        if (unit?.instanceId) {
          units.set(unit.instanceId, { playerId, zone, index });
        }
      });
    });
  });

  return units;
}

/**
 * Detect new unit-driven hand order grants and queue box-open overlays.
 */
export const useHandOrderGrantAnimations = (gameState, userId) => {
  const [activeGrants, setActiveGrants] = useState([]);
  const previousTriggerKeysRef = useRef(new Set());
  const previousUnitsRef = useRef(new Map());
  const timeoutRef = useRef(new Map());

  useEffect(() => {
    if (!gameState?.state || !userId) {
      return undefined;
    }

    const currentKeys = collectHandOrderGrantTriggerKeys(gameState);
    const newEntries = findNewHandOrderGrantEntries(gameState, previousTriggerKeysRef.current);

    if (newEntries.length > 0) {
      const previousUnits = previousUnitsRef.current;
      const queued = [];

      newEntries.forEach((entry) => {
        if (timeoutRef.current.has(entry.key)) {
          return;
        }

        const slot = resolveProviderUnitSlot(
          gameState,
          entry.providedBy,
          userId,
          previousUnits
        );
        if (!slot) {
          console.warn(
            `useHandOrderGrantAnimations: no slot for provider ${entry.providedBy.instanceId} (${entry.source})`
          );
          return;
        }

        queued.push({
          key: entry.key,
          zone: slot.zone,
          index: slot.index,
          providerName: entry.providedBy.name,
          orderName: entry.orderName,
          source: entry.source,
        });

        const timeoutId = setTimeout(() => {
          setActiveGrants((prev) => prev.filter((g) => g.key !== entry.key));
          timeoutRef.current.delete(entry.key);
        }, ANIMATION_TOTAL_MS);

        timeoutRef.current.set(entry.key, timeoutId);
      });

      if (queued.length > 0) {
        setActiveGrants((prev) => {
          const existing = new Set(prev.map((g) => g.key));
          const merged = [...prev];
          queued.forEach((grant) => {
            if (!existing.has(grant.key)) {
              merged.push(grant);
            }
          });
          return merged;
        });
      }
    }

    previousTriggerKeysRef.current = currentKeys;
    previousUnitsRef.current = snapshotBattlefieldUnits(gameState);

    return undefined;
  }, [gameState, userId]);

  useEffect(() => {
    return () => {
      timeoutRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
      timeoutRef.current.clear();
      previousTriggerKeysRef.current.clear();
      previousUnitsRef.current.clear();
    };
  }, []);

  const dismissGrant = (key) => {
    const timeoutId = timeoutRef.current.get(key);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutRef.current.delete(key);
    }
    setActiveGrants((prev) => prev.filter((g) => g.key !== key));
  };

  return { activeGrants, dismissGrant };
};
